import React, { useEffect } from 'react';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { useAccountCrumbs } from '@thd-olt-component-react/customer-account-navigation';
import { Metadata } from '@thd-olt-component-react/metadata';
import { RETURN_METHOD } from '@src/constants/general';
import BareboneSummary from '@src/components/return-summary/barebone/BareboneSummary';
import { useReturnStore } from '../stores/returnStore';
import { getReturnSummaryFieldsFromStorage } from '../utils/storage-helper';
import { ReturnSummary } from '../components/return-summary/ReturnSummary';
import { PageLayout } from '../components/shared/PageLayout';
import { setSummarySession, useSummaryStore } from '../stores/summaryStore';
import { triggerQualtricsSurvey } from '../utils/general';
import '../styles/returns.scss';

interface ReturnsProps {
  customerType: string;
  customerRole: string;
}

export const ReturnSummaryPage = ({
  customerRole,
  customerType,
}: ReturnsProps) => {
  const orderId = useSummaryStore((state) => state.orderId);
  const returnMethod = useSummaryStore((state) => state.summaryShipments)?.[0]
    ?.itemsToReturn?.[0]?.returnMethod;
  const isMilitaryExchangeGuest = useReturnStore(
    (state) => state.isMilitaryExchangeGuest,
  );
  const hasDuplicateReturns = useSummaryStore(
    (state) => state.hasDuplicateReturns,
  );
  const orderDetailsPath = useReturnStore((state) => state.orderDetailsPath);

  useEffect(() => {
    getReturnSummaryFieldsFromStorage().then((returnSummaryFields) => {
      if (!returnSummaryFields) return;
      setSummarySession(returnSummaryFields);
      triggerQualtricsSurvey();
    });
  }, []);

  const nonGuestCrumbs = [
    {
      path: '/myaccount/purchase-history',
      label: 'Purchase History',
    },
    {
      path: orderDetailsPath,
      label: 'Order Details',
    },
    {
      label: 'Return Summary',
      path: '/myaccount/return-summary',
    },
  ];

  const guestCrumbs = [
    {
      path: '/myaccount/guest-order-tracking',
      label: 'Track Your Order',
    },
    {
      label: 'Return Summary',
      path: '/myaccount/return-summary',
    },
  ];

  const crumbs = useAccountCrumbs(
    isMilitaryExchangeGuest ? guestCrumbs : nonGuestCrumbs,
  );

  // These tags will be used so Qualtrics can inject their survey component on a successful return on page render
  const showReturnsExperienceSurvey =
    returnMethod === RETURN_METHOD.UPS_DROP_OFF ||
    returnMethod === RETURN_METHOD.IN_STORE ||
    returnMethod === RETURN_METHOD.SORIS;

  const QualtricsSucessReturnSurvey = (
    <ErrorBoundary>
      <input type="hidden" id="OrderNumberId" value={orderId} />
      {showReturnsExperienceSurvey && (
        <div id="ReturnsExperience" className="qualtrics-survey" />
      )}
      {returnMethod === RETURN_METHOD.LORP && (
        <div id="LORPXPsurvey" className="qualtrics-survey" />
      )}
    </ErrorBoundary>
  );

  return (
    <PageLayout
      crumbs={crumbs}
      customerRole={customerRole}
      customerType={customerType}
      pageTitle="Return Summary"
    >
      <>
        <Metadata title="The Home Depot" />
        {orderId && !hasDuplicateReturns && <ReturnSummary />}
        {hasDuplicateReturns && <BareboneSummary />}
        {QualtricsSucessReturnSurvey}
      </>
    </PageLayout>
  );
};

ReturnSummaryPage.displayName = 'Return Summary';
